import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { getErrorMessage } from '@/lib/errorMessage';

/**
 * Will the weather let you shoot tonight? (ERI-1980)
 *
 * The verdict is about weather only; the calendar covers darkness, the Milky Way
 * and the moon. The page sends the location and which night, and reads back a
 * verdict the server has usually already computed on its schedule, so this
 * request is a table read in the common case.
 */

export type NightVerdict = 'go' | 'maybe' | 'no_go';
export type VerdictBlocker = 'cloud' | 'smoke' | 'wind' | 'nothing';

/**
 * How the panel should present this night.
 *
 *  - `verdict`    the model was confident; show the call
 *  - `conditions` it was not; show the numbers and let the photographer decide
 *  - `unreadable` the forecast itself is not usable
 */
export type VerdictDisplay = 'verdict' | 'conditions' | 'unreadable';

export interface NightConditions {
  bearing: number;
  samples: Array<{
    label: string;
    distanceKm: number;
    hours: Array<{
      time: string;
      cloudCover: number | null;
      low: number | null;
      mid: number | null;
      high: number | null;
      visibilityM: number | null;
      windSpeed: number | null;
      windGust: number | null;
    }>;
  }>;
  smoke: {
    aod: number | null;
    pm25: number | null;
    usAqi: number | null;
    level: string;
    blurb: string;
  };
  windowStart: string;
  windowEnd: string;
  windowMinutes: number;
}

export interface NightVerdictResult {
  verdict: NightVerdict | null;
  probability: number | null;
  confidence: number | null;
  blocker: VerdictBlocker | null;
  bestHours: string | null;
  shownAs: VerdictDisplay;
  conditions: NightConditions | null;
  reason?: string;
  /** Jev did not answer (timeout, outage). The conditions are still real. */
  modelUnavailable?: boolean;
  cached?: boolean;
}

interface UseNightVerdictProps {
  locationId?: string;
  latitude?: number;
  longitude?: number;
  timezone?: string;
  /** Which night. Defaults to tonight. */
  date?: Date;
  enabled?: boolean;
}

/**
 * The calendar date of `night` at the location, as YYYY-MM-DD.
 *
 * `toISOString()` gives the UTC date, which in the Americas rolls over to
 * tomorrow in the early evening, exactly when someone checks tonight's verdict.
 */
export const nightKeyFor = (night: Date, timezone: string): string => {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(night);
  } catch {
    // Unknown zone name: fall back to the browser's local date.
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${night.getFullYear()}-${pad(night.getMonth() + 1)}-${pad(night.getDate())}`;
  }
};

export const useNightVerdict = ({
  locationId,
  latitude,
  longitude,
  timezone = 'UTC',
  date,
  enabled = true,
}: UseNightVerdictProps) => {
  const [data, setData] = useState<NightVerdictResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user, isSubscribed } = useAuth();

  const nightKey = nightKeyFor(date ?? new Date(), timezone);

  const fetchVerdict = useCallback(async () => {
    if (!enabled || !user || !isSubscribed) return;
    if (!locationId || latitude === undefined || longitude === undefined) return;

    setLoading(true);
    setError(null);

    try {
      const { data: response, error: fnError } = await supabase.functions.invoke(
        'get-night-verdict',
        { body: { locationId, latitude, longitude, nightDate: nightKey } },
      );

      if (fnError) throw fnError;
      setData(response as NightVerdictResult);
    } catch (err) {
      setError(getErrorMessage(err));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [
    enabled,
    user,
    isSubscribed,
    locationId,
    latitude,
    longitude,
    nightKey,
  ]);

  useEffect(() => {
    fetchVerdict();
  }, [fetchVerdict]);

  return { data, loading, error, retry: fetchVerdict };
};
